// NicknameSheet.tsx — 닉네임 변경 (마이탭 "준비 중"이던 프로필 편집의 1차 배선, 2026-08-12)
//
// 서버 계약: change_nickname(p_new_nickname text) → jsonb
//   {ok:true, new_nickname} / {ok:false, error: invalid_length|same_nickname|
//   cooldown(+next_available_at)|duplicate|unauthenticated|profile_not_found}
//   길이·중복·30일 쿨타임·이력 기록은 전부 서버(RPC)가 정본 — 프론트 검사는 UX용 선반영일 뿐.
// 닉네임 규칙은 가입 화면(app/auth/signup)과 동일: 2~20자, 한글/영문/숫자/_.
import { useEffect, useState } from 'react';
import { CSModal, CSBtn } from '../_components/cs';
import { supabase } from '../../lib/supabase';

type Props = {
  open: boolean;
  onClose: () => void;
  /** 현재 닉네임 — 입력 초기값 + 동일 닉 사전 안내 */
  current: string;
  /** 변경 성공 후 호출 — me 캐시 갱신은 호출측 몫 */
  onChanged: (newNickname: string) => void;
};

// RPC 에러 코드 → 사용자 문구 (계약의 error 값과 1:1)
const ERROR_COPY: Record<string, string> = {
  invalid_length: '닉네임은 2~20자여야 해요.',
  same_nickname: '지금 쓰고 있는 닉네임이에요.',
  duplicate: '이미 사용 중인 닉네임이에요.',
  unauthenticated: '로그인이 필요해요.',
  profile_not_found: '프로필을 찾지 못했어요. 잠시 후 다시 시도해주세요.',
};

export function NicknameSheet({ open, onClose, current, onChanged }: Props) {
  const [value, setValue] = useState(current);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  // 확인 단계 — 성공하면 30일 락이 곧바로 걸리는데 되돌릴 방법이 없다.
  // 별도 모달을 겹치지 않고 시트 안에서 버튼만 바꾼다(중첩 모달은 iOS에서 배경 시트를 죽인 이력).
  const [confirming, setConfirming] = useState(false);

  // 열 때마다 현재 닉네임으로 리셋 (닫았다 열면 이전 입력·에러 잔존 방지)
  useEffect(() => {
    if (!open) return;
    setValue(current);
    setError('');
    setBusy(false);
    setConfirming(false);
  }, [open, current]);

  const trimmed = value.trim();
  // 가입 화면과 동일 규칙 — 서버가 정본이지만 제출 전 즉시 피드백용
  const lengthOk = trimmed.length >= 2 && trimmed.length <= 20;
  const charsOk = /^[a-zA-Z0-9가-힣_]*$/.test(trimmed);
  const changed = trimmed.toLowerCase() !== (current || '').trim().toLowerCase();
  const canSubmit = lengthOk && charsOk && changed && !busy;

  async function handleSubmit() {
    if (!canSubmit) return;
    // 첫 클릭은 확인만 받는다 — 여기서 RPC를 쏘면 30일 락이 즉시 걸린다.
    if (!confirming) {
      setError('');
      setConfirming(true);
      return;
    }
    setError('');
    setBusy(true);
    const { data, error: rpcError } = await supabase.rpc('change_nickname', {
      p_new_nickname: trimmed,
    });
    // 🔑 Supabase는 실패해도 throw가 아니라 { error }로 resolve — 반환값 검사가 유일한 방어선
    if (rpcError) {
      setError('변경에 실패했어요. 잠시 후 다시 시도해주세요.');
      setBusy(false);
      setConfirming(false); // 실패했으면 확인 단계도 풀어 준다 (고친 뒤 다시 확인받게)
      return;
    }
    const res = data as { ok?: boolean; error?: string; next_available_at?: string; new_nickname?: string } | null;
    if (!res?.ok) {
      if (res?.error === 'cooldown' && res.next_available_at) {
        const d = new Date(res.next_available_at);
        setError(`닉네임은 30일에 한 번 바꿀 수 있어요 · ${d.getMonth() + 1}월 ${d.getDate()}일부터 가능`);
      } else {
        setError(ERROR_COPY[res?.error ?? ''] || '변경에 실패했어요. 잠시 후 다시 시도해주세요.');
      }
      setBusy(false);
      setConfirming(false); // 실패했으면 확인 단계도 풀어 준다 (고친 뒤 다시 확인받게)
      return;
    }
    setBusy(false);
    onChanged(res.new_nickname || trimmed);
    onClose();
  }

  return (
    <CSModal open={open} onClose={onClose} title="닉네임 변경" sheet>
      <div style={{ padding: '4px 0 8px', display: 'flex', flexDirection: 'column', gap: 12 }}>
        <input
          value={value}
          onChange={(e) => { setValue(e.target.value); setConfirming(false); }} // 값을 고치면 확인은 무효
          placeholder="닉네임 (2~20자)"
          maxLength={20}
          disabled={busy}
          autoFocus
          style={{
            width: '100%', boxSizing: 'border-box', padding: '12px 14px',
            background: 'var(--cs-surface-2)', border: 'none', borderRadius: 'var(--cs-r-md)',
            fontSize: 15, color: 'var(--cs-ink)', fontFamily: 'inherit', outline: 'none',
          }}
        />
        <div style={{ fontSize: 11.5, color: 'var(--cs-mute)', lineHeight: 1.6 }}>
          한글·영문·숫자·_ 만 쓸 수 있어요 · 30일에 한 번 변경 가능
          <br />커뮤니티에 이미 쓴 글의 닉네임은 그대로 남아요.
        </div>
        {!charsOk && trimmed.length > 0 && (
          <div style={{ fontSize: 12, color: 'var(--cs-fall)' }}>한글/영문/숫자/_만 사용할 수 있어요.</div>
        )}
        {error && <div style={{ fontSize: 12, color: 'var(--cs-fall)' }}>{error}</div>}
        {/* 확인 단계 — 바꾸고 나면 30일간 되돌릴 수 없으므로 새 닉네임을 그대로 보여 주고 한 번 더 묻는다. */}
        {confirming && (
          <div style={{ fontSize: 13, color: 'var(--cs-ink)', lineHeight: 1.6, padding: '10px 12px', borderRadius: 'var(--cs-r-md)', background: 'var(--cs-surface-2)' }}>
            <b>‘{trimmed}’</b>(으)로 바꿀까요?
            <br />
            <span style={{ color: 'var(--cs-mute)' }}>바꾸면 30일 동안 다시 바꿀 수 없어요.</span>
          </div>
        )}
        {confirming ? (
          <div style={{ display: 'flex', gap: 8 }}>
            <CSBtn variant="secondary" onClick={() => setConfirming(false)} disabled={busy} style={{ flex: 1 }}>
              다시 고를게요
            </CSBtn>
            <CSBtn onClick={handleSubmit} disabled={!canSubmit} style={{ flex: 1 }}>
              {busy ? '변경 중…' : '네, 바꿀게요'}
            </CSBtn>
          </div>
        ) : (
          <CSBtn onClick={handleSubmit} disabled={!canSubmit} style={{ width: '100%' }}>
            변경하기
          </CSBtn>
        )}
      </div>
    </CSModal>
  );
}
